/**
 * WDW Motion (Animations-Briefing): Scroll-Reveals, Stagger (per CSS), Count-up, Header-Schatten, Badge-Pop.
 * - Ein IntersectionObserver für [data-reveal] und [data-reveal-group]; jedes Element nur einmal.
 * - Sicherheitsnetz: nach 2,5 s ist alles sichtbar. Neue Elemente (z. B. von ABlyft) über MutationObserver in main.
 * - Theme-Editor: keine Reveals (CSS), Count-ups zeigen den Endwert; Section-Events werden neu gescannt.
 * - prefers-reduced-motion: Count-ups sofort mit Endwert.
 */
(() => {
  if (window.wdwMotion) return;

  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const REVEAL = '[data-reveal], [data-reveal-group]';

  /** @param {Element} el */
  const countUp = (el) => {
    if (!(el instanceof HTMLElement) || el.dataset.counted) return;
    el.dataset.counted = 'true';
    const target = parseFloat(el.dataset.countup ?? '');
    if (Number.isNaN(target) || reduce || designMode) return;

    const decimals = parseInt(el.dataset.decimals ?? '0', 10) || 0;
    const prefix = el.dataset.prefix ?? '';
    const suffix = el.dataset.suffix ?? '';
    const final = el.textContent;
    const format = (/** @type {number} */ value) =>
      prefix + value.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
    const duration = 1200;
    const start = performance.now();

    const tick = (/** @type {number} */ now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = t < 1 ? format(target * eased) : final;
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  /** @param {Element} el */
  const reveal = (el) => {
    el.classList.add('is-revealed');
    if (el.hasAttribute('data-reveal-group')) {
      for (const child of el.children) if (child.hasAttribute('data-reveal')) child.classList.add('is-revealed');
    }
    for (const counter of el.querySelectorAll('[data-countup]')) countUp(counter);
    if (el.hasAttribute('data-countup')) countUp(el);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal(entry.target);
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
  );

  /** @param {ParentNode} scope */
  const scan = (scope) => {
    const items = scope.querySelectorAll(`${REVEAL}, [data-countup]`);
    for (const el of items) {
      if (el.classList.contains('is-revealed') || el.dataset.counted) continue;
      if (designMode) reveal(el);
      else observer.observe(el);
    }
  };

  /**
   * Sicherheitsnetz: Elemente, die bereits im oder über dem Viewport liegen, aber (z. B. weil der Observer nicht
   * gefeuert hat oder ABlyft sie ersetzt hat) noch versteckt sind, sichtbar schalten. Weiter unten liegende
   * Elemente behalten ihre Scroll-Einblendung.
   */
  const revealAll = () => {
    for (const el of document.querySelectorAll(REVEAL)) {
      if (el.classList.contains('is-revealed')) continue;
      if (el.getBoundingClientRect().top < window.innerHeight) reveal(el);
    }
  };

  const init = () => {
    scan(document);
    window.setTimeout(revealAll, 2500);

    // Nach dem Scrollen (entprellt) prüfen, ob etwas im Viewport versteckt geblieben ist
    let settle = 0;
    document.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(settle);
        settle = window.setTimeout(revealAll, 2500);
      },
      { passive: true, capture: true }
    );

    const main = document.querySelector('main');
    if (main) {
      let pending = 0;
      new MutationObserver(() => {
        if (pending) return;
        pending = window.setTimeout(() => {
          pending = 0;
          scan(main);
          window.setTimeout(revealAll, 2500);
        }, 200);
      }).observe(main, { childList: true, subtree: true });
    }

    // Header: mehr Schatten ab 24 px Scrolltiefe. Horizon scrollt .page-wrapper, nicht das Fenster.
    const header = document.querySelector('.wdw-header');
    if (header) {
      const wrapper = document.querySelector('.page-wrapper');
      const update = () =>
        header.classList.toggle('is-scrolled', Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0) > 24);
      document.addEventListener('scroll', update, { passive: true, capture: true });
      update();
    }

    // Zähler-Badge: einmal pro Session „Pop“ nach 1,2 s
    const badge = document.querySelector('.wdw-header__pill .wdw-count-badge');
    if (badge && !reduce) {
      let seen = false;
      try {
        seen = sessionStorage.getItem('wdw-badge-pop') === '1';
        sessionStorage.setItem('wdw-badge-pop', '1');
      } catch {
        // ohne Speicher: jedes Mal
      }
      if (!seen) window.setTimeout(() => badge.classList.add('is-popping'), 1200);
    }
  };

  // Theme-Editor: neu geladene Sections scannen
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) scan(event.target);
  });

  window.wdwMotion = { scan, reveal };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  root.classList.add('wdw-motion-ready');
})();
